import type { ReactNode } from 'react'
import { useI18n } from '../i18n'
import { useAuth, venueHasModule, type Module } from '../store/auth'
import { appPath, type AppPath } from '../lib/paths'
import {
  IconCalendar,
  IconChart,
  IconClipboard,
  IconCompass,
  IconHeadset,
  IconMonitor,
  IconMap,
  IconLayers,
  IconPeople,
  IconPulse,
  IconScan,
  IconSettings,
  IconTicket,
  IconToday,
  MarkPortage,
} from './icons'

/**
 * Les entrées de navigation, groupées par RYTHME.
 *
 * Chacune déclare la capacité qui l'ouvre, et la liste est FILTRÉE. C'est ce
 * qui rend les presets étroits utilisables : une tablette en preset `scan` ne
 * montre que « Scan », pas onze entrées dont dix rendent 403. Proposer un
 * écran pour le refuser ensuite est le plus sûr moyen de faire croire à une
 * panne — et d'apprendre à l'équipe que l'outil est cassé.
 *
 * `null` signifie « visible pour tout membre » : les réglages n'affichent que
 * ce que la personne a déjà le droit de voir.
 */
export type NavItem = {
  /**
   * La clé de `APP_PATHS`. Le chemin est DÉRIVÉ d'elle au rendu, jamais
   * déclaré à côté : deux sources pour une même URL se désynchronisent au
   * premier renommage, et rien ne le signale.
   */
  key: AppPath
  label: string
  Icon: (props: { size?: number }) => ReactNode
  /** `end` distingue « exactement / » de « commence par / ». */
  end?: boolean
  /**
   * Survit en mode SALLE.
   *
   * Le défaut est `false` : une entrée nouvelle n'apparaît pas dans une salle
   * tant que personne ne l'a décidé. L'inverse — tout garder sauf ce qu'on
   * exclut — ferait apparaître la prochaine entrée ajoutée, et ses requêtes
   * rendraient 404 sans que l'auteur comprenne pourquoi.
   */
  room?: boolean
  /*
   * DANS LA SALLE SEULEMENT, jamais au nuage. L'exploitation (Quentin, 01/10) :
   * un lieu a TOUJOURS son instance de salle, c'est elle qui pilote, et un
   * second tableau modifiable au nuage était la source des conflits de
   * synchronisation — deux pilotes pour les mêmes cartes.
   */
  roomOnly?: boolean
  /**
   * La capacité qui ouvre l'écran. `null` = tout membre.
   *
   * Une LISTE veut dire « l'une ou l'autre », jamais « toutes ». C'est
   * l'écran d'équipe qui l'a rendue nécessaire : il porte deux onglets aux
   * droits distincts — le planning demande `staff:assign`, les postes
   * demandent `team:manage` — et un gérant, qui n'a que le premier, doit
   * quand même voir l'entrée. Exiger les deux la lui aurait cachée ; n'en
   * exiger qu'une l'aurait ouverte à qui n'a ni l'un ni l'autre.
   */
  need: string | string[] | null
  /*
   * LE MODULE qui porte l'écran, quand ce n'est pas le socle (28/10). Une
   * capacité possédée par un module éteint disparaît déjà côté serveur ; ceci
   * couvre les capacités PARTAGÉES — `sessions:read` ouvre la journée
   * (billetterie) comme le planning (équipe).
   */
  module?: Module
}

export type NavGroup = { key: 'today' | 'sell' | 'setup'; label: string; items: NavItem[] }

/**
 * Les groupes de navigation, et pourquoi trois.
 *
 * Douze entrées à plat, c'est au-delà du seuil où l'œil balaie au lieu de
 * viser. L'axe retenu est le RYTHME — ce qu'on ouvre chaque heure, ce qui fait
 * vendre, ce qu'on règle une fois — et pas le domaine.
 *
 * Un découpage par domaine (« ventes » / « exploitation ») mettrait les sites
 * web, édités par trimestre, dans le même tiroir que l'exploitation temps réel
 * qu'on garde ouverte pendant qu'une salle tourne : le moins et le plus
 * consultés voisins. Le rythme est aussi ce que les capacités encodent déjà —
 * la personne au scan en a une, le gérant vingt-cinq.
 *
 * Les sites web n'apparaissent nulle part : un site est un onglet de
 * l'expérience qu'il vend, depuis qu'un écran séparé obligeait à choisir dans
 * une liste déroulante l'expérience dont on venait.
 */
export function useNavGroups(): {
  groups: NavGroup[]
  items: NavItem[]
  studio: NavItem | null
  showGroupLabels: boolean
} {
  const { t } = useI18n()
  const venue = useAuth((s) => s.venues.find((v) => v.id === s.venueId))
  /*
   * UN LIEU SANS BILLET N'A PAS DE SCAN.
   *
   * `!== false` et non `=== true` : une instance antérieure au champ ne le
   * renvoie pas, et elle exige forcément un billet — c'était le seul
   * comportement. Lire `=== true` ferait disparaître le scan de toutes les
   * salles le jour d'une mise à jour, sans rien signaler.
   */
  const ticketRequired = venue?.ticketRequired !== false
  const isSuperadmin = useAuth((s) => s.user?.isSuperadmin ?? false)
  /* Sous bridage volontaire, le studio a renoncé à sa console : l'API la
     refuse, et la proposer quand même rendrait un 403 sur une entrée qu'on
     vient de cliquer. */
  const viewAs = useAuth((s) => s.viewAs)
  const multiVenue = useAuth((s) => s.venues.length > 1)
  const room = useAuth((s) => s.mode === 'room')
  const hasModule = (module: Module | undefined) => !module || venueHasModule(venue, module)
  const can = (capability: string | string[] | null) => {
    if (capability === null) return true
    const held = venue?.can ?? []
    return Array.isArray(capability)
      ? capability.some((one) => held.includes(one))
      : held.includes(capability)
  }

  const defined: NavGroup[] = [
    {
      key: 'today',
      label: t.nav.groupToday,
      items: [
        /*
         * Le TABLEAU DE BORD n'apparaît que si la personne voit PLUS D'UN
         * lieu, ou si c'est le studio.
         *
         * Pour les neuf exploitants sur dix qui n'ont qu'une salle, il ne
         * poserait qu'une question sans alternative — « lequel ? » — avant la
         * seule réponse possible. Même règle que le sélecteur de lieu de la
         * barre latérale, qui ne s'affiche pas non plus pour un lieu unique,
         * et la racine redirige alors droit sur la journée.
         *
         * Il est dans « La journée » et non dans un module de rapports :
         * quelqu'un qui exploite trois salles l'ouvre le matin, à la place de
         * la journée d'une seule.
         */
        ...(multiVenue || isSuperadmin
          ? [
              {
                key: 'dashboard' as const,
                label: t.nav.dashboard,
                Icon: IconLayers,
                end: true,
                need: 'sessions:read',
                module: 'billetterie' as const,
              },
            ]
          : []),
        /*
         * AUJOURD'HUI : la journée du lieu où l'on est.
         *
         * Elle était la racine. Le nom compte : « Tableau de bord » sur une
         * page qui montre les séances de l'après-midi d'une salle promettait
         * une vue d'ensemble et donnait un horaire, et c'est exactement ce qui
         * rendait l'accueil illisible pour un compte à plusieurs lieux.
         */
        { key: 'today', label: t.nav.today, Icon: IconToday, need: 'sessions:read', module: 'billetterie' },
        { key: 'calendar', label: t.nav.calendar, Icon: IconCalendar, need: 'sessions:read', module: 'billetterie' },
        /*
         * L'ÉQUIPE : qui travaille ici, ce qu'ils peuvent, et quand.
         *
         * Les deux moitiés vivaient à deux endroits — le planning en
         * navigation, les postes dans un onglet des réglages — et l'état vide
         * du premier disait « ajoutez quelqu'un dans Réglages › Équipe ».
         * Une instruction qui traverse deux écrans est le signe que les deux
         * n'en font qu'un : on embauche, on donne un poste, on met au
         * planning, et c'est une seule suite de gestes.
         *
         * Elle est dans « La journée » et non dans « Configurer » parce que
         * l'onglet qui s'ouvre par défaut est le PLANNING : on le pose une
         * fois par semaine, ce qui est le rythme de l'exploitation. Les
         * postes, réglés une fois, sont le second onglet — un écran peut
         * contenir deux rythmes tant que le premier décide de sa place.
         *
         * Les deux capacités, en « l'une ou l'autre » : un gérant porte
         * `staff:assign` sans `team:manage`, et il n'y verra que le planning.
         */
        {
          key: 'team',
          label: t.team.title,
          Icon: IconPeople,
          need: ['staff:assign', 'team:manage'],
        },
        /*
         * Le scan est une ACTION avant d'être un écran, et il vit donc dans la
         * barre du haut à côté de la vente — les deux se font au comptoir, pas
         * en consultant une page.
         *
         * Il reste ici pour QUI N'A QUE ÇA. Une tablette en preset `scan` ne
         * porte que `checkin` : sans cette entrée, sa navigation se réduirait à
         * « Réglages », et `useHomePath` — qui envoie chacun sur sa première
         * entrée ouvrable — ferait atterrir la borne de la porte sur l'écran
         * des réglages. C'est le piège de la racine-destination, déjà rencontré
         * une fois.
         *
         * La condition porte sur les AUTRES écrans du jour, pas sur une liste
         * de rôles : elle reste juste si un preset s'ajoute.
         */
        ...(can('checkin') && ticketRequired && !can('sessions:read') && !can('bookings:read')
          ? [{ key: 'scan' as const, label: t.scan.title, Icon: IconScan, need: 'checkin', module: 'billetterie' as const }]
          : []),
        /* L'exploitation est dans « aujourd'hui » et non dans un module
           technique : c'est l'écran qu'on garde ouvert pendant qu'un groupe
           est en salle. */
        { key: 'ops', label: t.ops.title, Icon: IconPulse, need: 'ops:read', room: true, roomOnly: true },
      ],
    },
    {
      key: 'sell',
      label: t.nav.groupSell,
      items: [
        { key: 'bookings', label: t.nav.bookings, Icon: IconTicket, need: 'bookings:read', module: 'billetterie' },
        { key: 'customers', label: t.nav.customers, Icon: IconPeople, need: 'customers:read', module: 'marketing' },
        /* CAMPAGNES MASQUÉES (décision du 25 septembre) : le module existe, mais
           son envoi n'est branché à aucun fournisseur réel, et une entrée qui ne
           produit rien s'apprend comme un outil cassé. La route est retirée
           avec l'entrée — une page absente du menu reste une destination. Le
           code reste, et les parcours automatiques tournent toujours. */
        /*
         * L'analytique est dans « Vendre » et non dans un module de rapports
         * séparé : la question qu'elle répond — « qu'est-ce qui se vend, à
         * qui, quand » — est celle qu'on se pose en préparant une mise en
         * vente ou une campagne, pas celle du matin.
         *
         * Elle demande `finance:read` : c'est de l'argent AGRÉGÉ, la même
         * frontière que le revenu du tableau de bord. Quelqu'un au comptoir
         * voit le prix d'un billet sans voir le chiffre du mois.
         */
        { key: 'analytics', label: t.nav.analytics, Icon: IconChart, need: 'finance:read' },
      ],
    },
    {
      key: 'setup',
      label: t.nav.groupSetup,
      items: [
        /*
         * CE QUI SE RÈGLE D'UN LIEU, une entrée par objet (refonte du 29/09).
         *
         * Une salle se réglait à trois endroits, un moteur à quatre, et deux
         * écrans s'appelaient « Parc » sans parler de la même chose. Chaque
         * objet a maintenant sa page : les expériences, les salles (serveur,
         * clé, réseau, carte), les casques (parc, installation, capacité),
         * l'accueil (bornes et écrans), les réglages du lieu.
         */
        { key: 'experiences', label: t.nav.experiences, Icon: IconCompass, need: 'catalogue:read' },
        { key: 'rooms', label: t.nav.rooms, Icon: IconMap, need: 'ops:read', room: true },
        /* En salle aussi : c'est là que le balayage du réseau a un sens. */
        /* EN SALLE AUSSI depuis le 07/10, mais pour les PAGES DYNAMIQUES
           seulement (leur adresse locale). Les bornes et les écrans TV n'y
           sont pas : leurs parcours n'y sont pas montés, et un écran déclaré
           là ne restait que dans la base de la salle (29/09). */
        { key: 'welcome', label: t.nav.welcome, Icon: IconMonitor, need: 'ops:read', room: true },
        { key: 'onboarding', label: t.ob.title, Icon: IconClipboard, need: 'onboarding:manage' },
        /*
         * LES RÉGLAGES NE SURVIVENT PLUS EN MODE SALLE, et la raison n'est pas
         * qu'ils sont inutiles — c'est qu'ils MENTENT là-bas.
         *
         * Un de leurs deux onglets est mort : les intégrations appellent
         * `/api/engines`, qui n'est pas monté sur une salle. L'autre est pire,
         * parce qu'il a l'air de marcher : la fiche du lieu s'enregistre dans
         * la base LOCALE, et la salle ne repousse au nuage que des événements
         * de visite — `visit.create`, `visit.move`, `visit.pair`,
         * `visit.group`, et rien d'autre. Un gérant qui corrige le nom de son
         * lieu depuis l'appareil de salle voit « enregistré », et le nuage ne
         * l'apprend jamais. Les deux bases divergent en silence.
         */
        { key: 'settings', label: t.settings.title, Icon: IconSettings, need: null },
      ],
    },
  ]

  /*
   * Un groupe vide DISPARAÎT, en-tête comprise.
   *
   * C'est la même règle que pour les entrées : un intertitre « Vendre » sans
   * rien dessous se lit comme un chargement qui n'a pas fini, ou comme un
   * outil cassé. La personne au scan voit un seul groupe d'un seul élément.
   */
  /*
   * Le MODE filtre AVANT la capacité, et ce n'est qu'un confort.
   *
   * La vraie frontière est le montage des routeurs : ce qui n'est pas monté
   * rend 404, quelle que soit l'URL tapée. Ce filtre évite seulement de
   * proposer une destination dont toutes les requêtes échoueront — « proposer
   * un écran pour le refuser ensuite apprend à l'équipe que l'outil est
   * cassé ».
   */
  const groups = defined
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) => (room ? item.room === true : !item.roomOnly) && can(item.need) && hasModule(item.module),
      ),
    }))
    .filter((group) => group.items.length > 0)

  /*
   * La console du studio, tout en bas et pour le studio seulement.
   *
   * Elle n'est PAS gouvernée par une capacité de venue : c'est de la
   * plateforme. Un propriétaire de lieu ne doit pas la voir, même avec les
   * vingt-cinq capacités — ce n'est pas son outil. Elle vit HORS des groupes
   * pour la même raison : elle ne relève pas du rythme d'un lieu.
   */
  /* Au nuage seulement : la console n'existe pas dans une salle, même pour
     le studio qui s'y connecte. */
  const studio: NavItem | null =
    isSuperadmin && !viewAs && !room
      ? { key: 'studio', label: t.admin.title, Icon: MarkPortage, need: null }
      : null

  const items = [...groups.flatMap((group) => group.items), ...(studio ? [studio] : [])]

  /*
   * Les intertitres n'apparaissent qu'à partir de cinq destinations.
   *
   * Grouper ne paie que quand il y a de quoi grouper : une tablette en preset
   * `scan` voit « Scan » et « Réglages », et deux intertitres au-dessus de
   * deux entrées font quatre lignes pour deux endroits — c'est-à-dire plus
   * lourd que la liste qu'on cherchait à alléger. En dessous de cinq, on voit
   * tout d'un coup et l'œil n'a rien à repérer.
   *
   * Le seuil porte sur les GROUPES, pas sur `items` : la console du studio
   * vit hors des groupes, donc elle ne doit pas faire apparaître des
   * intertitres pour les autres.
   */
  const groupItemCount = groups.reduce((total, group) => total + group.items.length, 0)

  return { groups, items, studio, showGroupLabels: groupItemCount > 4 }
}

/**
 * L'écran d'accueil de CETTE personne.
 *
 * Le tableau de bord demande `sessions:read`, que le preset `scan` n'a pas :
 * une tablette qui se connectait atterrissait sur « Something went wrong. »,
 * avec une barre latérale correcte à côté. Le filtrage de la navigation ne
 * suffit donc pas — la racine est une destination comme une autre, et il faut
 * l'envoyer sur la première entrée que la personne peut ouvrir.
 */
export function useHomePath(): string {
  const { locale } = useI18n()
  const { items } = useNavGroups()
  const first = items[0]
  return first ? appPath(first.key, locale) : appPath('settings', locale)
}

/** Une rubrique de la console : un onglet de `/studio`, désigné par `?t=`. */
export type ConsoleItem = {
  tab: string
  label: string
  Icon: (props: { size?: number }) => ReactNode
}

/**
 * LES RUBRIQUES DE LA CONSOLE PORTAGE, dans le menu de gauche.
 *
 * Elles étaient huit onglets empilés en haut d'une seule page, derrière une
 * entrée « Studio » perdue au bas du menu d'un lieu. La console est un ESPACE,
 * pas une page : on y entre par le sélecteur, et le menu de gauche devient le
 * sien — comme on change d'espace de travail dans Slack ou Vercel.
 *
 * Même ordre que l'ancienne barre d'onglets : on ouvre sur « qui exploite
 * quoi », et les listes de détail suivent.
 */
export function useConsoleItems(): ConsoleItem[] {
  const { t } = useI18n()
  return [
    { tab: 'board', label: t.admin.board, Icon: IconChart },
    { tab: 'titles', label: t.admin.titles, Icon: IconCompass },
    { tab: 'fleet', label: t.fleet.inventory, Icon: IconHeadset },
    { tab: 'engines', label: t.eng.catalogue, Icon: IconLayers },
    { tab: 'game', label: t.game.title, Icon: IconChart },
    { tab: 'diagnostics', label: t.diag.title, Icon: IconPulse },
    { tab: 'identity', label: t.studio.administration, Icon: IconSettings },
  ]
}
